import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SupportTicketsContainer from './container';
import { useGetTicketsQuery } from './services';

const SupportTickets = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Pagination state (0-indexed page for CustomPagination UI component)
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  // Filters & Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedSource, setSelectedSource] = useState('all');

  // Reset to page 0 whenever filter or search query changes
  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [searchQuery, selectedStatus, selectedCategory, selectedPriority, startDate, endDate, selectedSource]);

  // Fetch Tickets API query
  const queryParams = {
    status: selectedStatus === 'awaiting' ? 'all' : selectedStatus,
    category: selectedCategory,
    priority: selectedPriority,
    search: searchQuery.trim(),
    start_date: startDate,
    end_date: endDate,
    page: paginationModel.page + 1, // Convert 0-indexed UI page to 1-indexed API page
    perPage: paginationModel.pageSize,
  };

  const { data: ticketsData, isLoading, isError, refetch } = useGetTicketsQuery(queryParams);

  const rawTickets = ticketsData?.data?.records ?? ticketsData?.records ?? [];
  const statistics = ticketsData?.data?.statistics ?? ticketsData?.statistics ?? {};
  const totalCount = ticketsData?.data?.totalCount ?? ticketsData?.totalCount ?? rawTickets.length;

  // Filter client side to guarantee exact filtering regardless of backend response structure
  const filteredTickets = rawTickets.filter((ticket) => {
    // 1. Status Filter
    if (selectedStatus && selectedStatus !== 'all') {
      if (selectedStatus === 'awaiting') {
        if (ticket.status !== 'awaiting_vendor' && ticket.status !== 'awaiting_admin') {
          return false;
        }
      } else if (ticket.status && ticket.status.toLowerCase() !== selectedStatus.toLowerCase()) {
        return false;
      }
    }

    // 2. Category Filter
    if (selectedCategory && selectedCategory !== 'all') {
      if (ticket.category && ticket.category.toLowerCase() !== selectedCategory.toLowerCase()) {
        return false;
      }
    }

    // 3. Priority Filter
    if (selectedPriority && selectedPriority !== 'all') {
      if (ticket.priority && ticket.priority.toLowerCase() !== selectedPriority.toLowerCase()) {
        return false;
      }
    }

    // 4. Source Filter
    if (selectedSource && selectedSource !== 'all') {
      const role = (ticket.creator?.role || '').toLowerCase();
      if (selectedSource === 'restaurant_admin' && !role.includes('admin')) return false;
      if (selectedSource === 'rider' && !role.includes('rider')) return false;
    }

    return true;
  });

  const handleOpenCreateView = () => {
    navigate('/support-tickets/create');
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedStatus('all');
    setSelectedCategory('all');
    setSelectedPriority('all');
    setStartDate('');
    setEndDate('');
    setSelectedSource('all');
    setPaginationModel({ page: 0, pageSize: 10 });
  };

  return (
    <SupportTicketsContainer
      tickets={filteredTickets}
      statistics={statistics}
      totalCount={totalCount}
      loading={isLoading}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      selectedStatus={selectedStatus}
      setSelectedStatus={setSelectedStatus}
      selectedCategory={selectedCategory}
      setSelectedCategory={setSelectedCategory}
      selectedPriority={selectedPriority}
      setSelectedPriority={setSelectedPriority}
      startDate={startDate}
      setStartDate={setStartDate}
      endDate={endDate}
      setEndDate={setEndDate}
      selectedSource={selectedSource}
      setSelectedSource={setSelectedSource}
      paginationModel={paginationModel}
      setPaginationModel={setPaginationModel}
      handleClearFilters={handleClearFilters}
      handleCreateClick={handleOpenCreateView}
    />
  );
};

export default SupportTickets;
